import * as React from 'react';
import { useSelector } from 'react-redux';
import { FavouriteReplaceDomainNodes, FavouriteDefinition, TextCollection, AdminInfoData } from 'app/types-shared';
import { STATE } from 'app/constants';
import { filtersTranslations } from './constants';
import { FiltersPanel } from 'app/components/filters/filters-panel/filters-panel';
import { ButtonInline } from 'ui';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import {
  useActionCreators,
  useFilterEventHandlers,
  useLoadFiltersAndAdvancedSearchTypesEffect,
  useUpdateConditionsAndConnectionsFromSimpleFormEffect,
  useUpdateFiltersFromDataEffect,
  useUserProfile,
  useInstanceSimpleForm
} from 'app/area/filters/hooks';
import { 
  selectAdvancedSearchFields, selectAdvancedSearchTypes, selectCurrentList, 
  selectFilterDomains, selectOriginalDomainNodeOptions, 
  selectRemoveDomainNodeOptions, selectReplaceDomainNodeOptions   
} from 'app/area/filters/selectors';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { ReactNode } from 'react';
import { reportTypes } from 'app/area/favourite/constants';

export interface FiltersProps {
  definition: FavouriteDefinition;
  workingDefinition: FavouriteDefinition;
  hideFilterReplacements?: boolean;
  adminPanelAccess?: AdminInfoData;
  updateDefinition: (transform?: (definition: FavouriteDefinition) => FavouriteDefinition) => void;
  fetchingUpdateReplaceDomainNodes?: boolean;
  fetchingExtendedReplaceDomainNodes?: boolean;
  updateReplaceDomainNodes?: (transform: (definition: FavouriteDefinition) => FavouriteDefinition, onSuccess: () => void) => void;
  loadExtendedReplaceDomainNodes?: (favouriteId: number, onSuccess: (replaceDomainNodes: FavouriteReplaceDomainNodes[]) => void) => void;
  locale: string;
  children?: ReactNode;
}

export function FiltersContainer({
  definition,
  workingDefinition,
  hideFilterReplacements,
  adminPanelAccess,
  updateDefinition,
  fetchingUpdateReplaceDomainNodes,
  fetchingExtendedReplaceDomainNodes,
  updateReplaceDomainNodes,
  loadExtendedReplaceDomainNodes,
  locale,
  children
}: FiltersProps) {

  const instanceKey = useInstanceKey();

  const translation = useTranslation(STATE.filters, filtersTranslations);
  const texts: TextCollection = useSelector(state => state[STATE.filters]?.texts);

  const {
    simpleForm,
    validate,
    validation,
    SimpleField
  } = useInstanceSimpleForm({ texts });

  const userProfile = useUserProfile();
  const currentList = useSelector(selectCurrentList(instanceKey));
  const advancedSearchFields = useSelector(selectAdvancedSearchFields(instanceKey));
  const originalDomainNodeOptions = useSelector(selectOriginalDomainNodeOptions(instanceKey));
  const replaceDomainNodeOptions = useSelector(selectReplaceDomainNodeOptions(instanceKey));
  const removeDomainNodeOptions = useSelector(selectRemoveDomainNodeOptions(instanceKey));
  const advancedSearchTypes = useSelector(selectAdvancedSearchTypes(instanceKey));
  const filterDomains = useSelector(selectFilterDomains(instanceKey));

  const {
    loadAdvancedSearchFields,
    loadReplaceDomainNodesFields,
    loadOriginalDomainNodesFields,
    loadRemoveNodesFields,
    setFilterOption,
    setFilterOpenState
  } = useActionCreators();

  const {
    onFilterMenuOption,
    onAddFilters,
    onRemoveFilters,
    onUpdateFilterReplacements,
    clear
  } = useFilterEventHandlers(simpleForm, currentList, advancedSearchTypes);

  const selectedReport = definition?.reports?.find(x => x.report?.v === definition.selectedReport?.v);
  const selectedReportType = selectedReport?.report?.v;
  const selectedReportId = selectedReportType === reportTypes.dashboard && selectedReport?.dashboard?.v;

  useLoadFiltersAndAdvancedSearchTypesEffect(selectedReportType, selectedReportId);
  useUpdateFiltersFromDataEffect(definition, filterDomains, advancedSearchTypes, translation);
  
  useUpdateConditionsAndConnectionsFromSimpleFormEffect(
    updateDefinition, currentList, simpleForm?.editing?.values
  );

  const clearFiltersButton = <ButtonInline onClick={clear}>{translation.clear_filters}</ButtonInline>;

  return currentList && currentList.length > 0 ?
    <FiltersPanel
      favouriteDefinition={definition}
      workingDefinition={workingDefinition}
      translation={translation}
      userProfile={userProfile}
      advancedSearchFields={advancedSearchFields}
      originalDomainNodeOptions={originalDomainNodeOptions}
      replaceDomainNodeOptions={replaceDomainNodeOptions}
      removeDomainNodeOptions={removeDomainNodeOptions}
      validate={validate}
      onRemove={onRemoveFilters}
      editingValues={simpleForm?.editing?.values}
      filtersDefinition={currentList}
      onAdvancedSearchFields={loadAdvancedSearchFields}
      onLoadReplaceDomainNodesFields={loadReplaceDomainNodesFields}
      onLoadOriginalDomainNodesFields={loadOriginalDomainNodesFields}
      onLoadRemoveNodesFields={loadRemoveNodesFields}
      onAddFilters={onAddFilters}
      onFilterMenuOption={onFilterMenuOption}
      onFilterOptionType={setFilterOption}
      validation={validation}
      locale={locale}
      setOpenState={setFilterOpenState}
      SimpleField={SimpleField}
      clearFiltersButton={clearFiltersButton}
      children={children}
      showAddFilters={true}
      hideFilterReplacements={hideFilterReplacements}
      adminPanelAccess={adminPanelAccess}
      onUpdateFilterReplacements={onUpdateFilterReplacements}
      updateReplaceDomainNodes={updateReplaceDomainNodes}
      fetchingUpdateReplaceDomainNodes={fetchingUpdateReplaceDomainNodes}
      fetchingExtendedReplaceDomainNodes={fetchingExtendedReplaceDomainNodes}
      loadExtendedReplaceDomainNodes={loadExtendedReplaceDomainNodes} />
    : null;
}
